import { Filter, Inbox, Timer, User, X } from 'lucide-react'
import { cn } from '@/utils/cn'
import { PAGE_TOOLBAR } from '@/components/ui/page-toolbar'
import { FINANCE_REQUEST_STATUSES } from '@/services/financeService'
import { formatFinanceStatus, type QueueSlaFilter } from '@/hooks/useFinanceiroPage'
import type { FinanceAssignee } from '@/services/financeService'

const selectClass = cn(PAGE_TOOLBAR.select, 'w-full')

type Props = {
  open: boolean
  onClose: () => void
  onClearAll: () => void
  queueStatus: string
  setQueueStatus: (v: string) => void
  queueType: string
  setQueueType: (v: string) => void
  queueSla: QueueSlaFilter
  setQueueSla: (v: QueueSlaFilter) => void
  queueAssignee: string
  setQueueAssignee: (v: string) => void
  queueUnassigned: boolean
  setQueueUnassigned: (v: boolean) => void
  assignees: FinanceAssignee[]
  typeOptions: { key: string; label: string }[]
}

export function FinanceQueueFiltersPanel({
  open,
  onClose,
  onClearAll,
  queueStatus,
  setQueueStatus,
  queueType,
  setQueueType,
  queueSla,
  setQueueSla,
  queueAssignee,
  setQueueAssignee,
  queueUnassigned,
  setQueueUnassigned,
  assignees,
  typeOptions,
}: Props) {
  if (!open) return null

  const assigneeValue = queueUnassigned ? '__unassigned' : queueAssignee

  return (
    <>
      <div className='fixed inset-0 z-40 bg-black/40 md:hidden' aria-hidden onClick={onClose} />
      <div
        role='dialog'
        aria-label='Filtros da fila financeira'
        className={cn(
          'z-50 rounded-xl border border-[var(--glass-border)] bg-[var(--bg-card)] p-4 shadow-2xl',
          'fixed inset-x-4 bottom-4 max-h-[70vh] overflow-y-auto md:absolute md:inset-auto md:right-0 md:top-full md:mt-2 md:w-80',
        )}
      >
        <div className='mb-4 flex items-center justify-between'>
          <h3 className='text-sm font-semibold text-text-primary'>Filtros da fila</h3>
          <button
            type='button'
            onClick={onClose}
            className='flex h-8 w-8 items-center justify-center rounded-lg text-text-muted hover:bg-white/[0.06] hover:text-text-primary'
            aria-label='Fechar filtros'
          >
            <X size={16} />
          </button>
        </div>

        <div className='flex flex-col gap-4'>
          <label className='flex flex-col gap-1.5'>
            <span className='flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-text-muted'>
              <Inbox size={13} />
              Status
            </span>
            <select
              className={selectClass}
              value={queueStatus}
              onChange={(e) => setQueueStatus(e.target.value)}
              aria-label='Filtrar por status'
            >
              <option value=''>Todos os status</option>
              {FINANCE_REQUEST_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {formatFinanceStatus(status)}
                </option>
              ))}
            </select>
          </label>

          <label className='flex flex-col gap-1.5'>
            <span className='flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-text-muted'>
              <Filter size={13} />
              Tipo
            </span>
            <select
              className={selectClass}
              value={queueType}
              onChange={(e) => setQueueType(e.target.value)}
              aria-label='Filtrar por tipo'
            >
              <option value=''>Todos os tipos</option>
              {typeOptions.map((type) => (
                <option key={type.key} value={type.key}>
                  {type.label}
                </option>
              ))}
            </select>
          </label>

          <label className='flex flex-col gap-1.5'>
            <span className='flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-text-muted'>
              <Timer size={13} />
              SLA
            </span>
            <select
              className={selectClass}
              value={queueSla}
              onChange={(e) => setQueueSla(e.target.value as QueueSlaFilter)}
              aria-label='Filtrar SLA'
            >
              <option value=''>Todos</option>
              <option value='overdue'>Vencido</option>
              <option value='due_soon'>Vence em 24h</option>
              <option value='ok'>No prazo</option>
            </select>
          </label>

          <label className='flex flex-col gap-1.5'>
            <span className='flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-text-muted'>
              <User size={13} />
              Responsável
            </span>
            <select
              className={selectClass}
              value={assigneeValue}
              onChange={(e) => {
                const value = e.target.value
                if (value === '__unassigned') {
                  setQueueUnassigned(true)
                  setQueueAssignee('')
                } else {
                  setQueueUnassigned(false)
                  setQueueAssignee(value)
                }
              }}
              aria-label='Filtrar responsável'
            >
              <option value=''>Todos responsáveis</option>
              <option value='__unassigned'>Sem responsável</option>
              {assignees.map((assignee) => (
                <option key={assignee.id} value={assignee.id}>
                  {assignee.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <button
          type='button'
          onClick={onClearAll}
          className='mt-4 w-full rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-xs font-medium text-red-500 transition-colors hover:bg-red-500/20 dark:text-red-400'
        >
          Limpar todos os filtros
        </button>
      </div>
    </>
  )
}
